import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { Colors } from "../utils/colors";
import { adminFetch } from "../services/adminFetch";
import { formatAdminDateTime } from "../utils/adminDateTime";
import AdminPageContainer from "../components/AdminPageContainer";
import ResponsiveDataTable from "../components/ResponsiveDataTable";
import TableLoadingOverlay from "../components/TableLoadingOverlay";
import {
  adminPageSx,
  contentCardSx,
  filterStackSx,
  mobileActionGroupSx,
  textWrapSx
} from "../utils/adminResponsive";

const formatDateTime = (value) => formatAdminDateTime(value, { includeSeconds: false, fallback: "-" });

const initialForm = {
  assigned_draw_id: ""
};

export default function PresenterAccounts() {
  const [formData, setFormData] = useState(initialForm);
  const [presenterAccounts, setPresenterAccounts] = useState([]);
  const [drawOptions, setDrawOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [latestCredentials, setLatestCredentials] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: ""
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      severity,
      message
    });
  };

  const copyValue = async (label, value) => {
    try {
      await navigator.clipboard.writeText(String(value || ""));
      showSnackbar(`${label} copied`);
    } catch (_error) {
      showSnackbar(`Unable to copy ${label.toLowerCase()}`, "error");
    }
  };

  const loadPresenterAccounts = useCallback(async () => {
    setLoading(true);

    try {
      const [accountsResponse, drawsResponse] = await Promise.all([
        adminFetch("/api/admin/presenter-accounts", { method: "GET" }),
        adminFetch("/api/admin/draws", { method: "GET" })
      ]);

      if (accountsResponse?.status) {
        setPresenterAccounts(accountsResponse.presenter_accounts || []);
      } else {
        showSnackbar(accountsResponse?.message || "Failed to load presenter accounts", "error");
      }

      if (drawsResponse?.status) {
        setDrawOptions(
          (drawsResponse.draws || []).filter(
            (draw) => String(draw.status || "").toLowerCase() !== "completed"
          )
        );
      }
    } catch (error) {
      console.log(error);
      showSnackbar("Failed to load presenter account data", "error");
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadPresenterAccounts();
  }, [loadPresenterAccounts]);

  const filteredAccounts = useMemo(() => {
    const normalizedSearch = String(search || "").trim().toLowerCase();

    if (!normalizedSearch) {
      return presenterAccounts;
    }

    return presenterAccounts.filter((account) => {
      const presenterId = String(account.presenter_id || "").toLowerCase();
      const drawCode = String(account.assigned_draw?.draw_code || "").toLowerCase();
      const status = String(account.status || "").toLowerCase();

      return (
        presenterId.includes(normalizedSearch) ||
        drawCode.includes(normalizedSearch) ||
        status.includes(normalizedSearch)
      );
    });
  }, [presenterAccounts, search]);

  const activeCount = useMemo(
    () =>
      presenterAccounts.filter(
        (account) => String(account.status || "").toLowerCase() === "active"
      ).length,
    [presenterAccounts]
  );

  const createPresenterAccount = async () => {
    if (!formData.assigned_draw_id) {
      showSnackbar("Please choose a draw for the presenter", "error");
      return;
    }

    setSaving(true);

    try {
      const response = await adminFetch("/api/admin/presenter-accounts", {
        method: "POST",
        body: JSON.stringify({
          assigned_draw_id: Number(formData.assigned_draw_id)
        })
      });

      if (!response?.status) {
        showSnackbar(response?.message || "Failed to create presenter account", "error");
        setSaving(false);
        return;
      }

      setFormData(initialForm);
      setLatestCredentials(response.credentials || null);
      showSnackbar("Presenter account created successfully");
      await loadPresenterAccounts();
    } catch (error) {
      console.log(error);
      showSnackbar("Failed to create presenter account", "error");
    }

    setSaving(false);
  };

  const presenterColumns = [
    {
      key: "presenter_id",
      label: "Presenter ID",
      render: (item) => (
        <Stack direction="row" spacing={1} alignItems="center" sx={mobileActionGroupSx}>
          <Typography sx={textWrapSx}>{item.presenter_id}</Typography>
          <IconButton
            size="small"
            aria-label={`Copy presenter ID ${item.presenter_id}`}
            onClick={() => copyValue("Presenter ID", item.presenter_id)}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Stack>
      )
    },
    {
      key: "draw",
      label: "Assigned Draw",
      render: (item) => item.assigned_draw?.draw_code || "-"
    },
    {
      key: "draw_date",
      label: "Draw Date",
      render: (item) => formatDateTime(item.assigned_draw?.draw_date)
    },
    {
      key: "status",
      label: "Status",
      render: (item) => (
        <Chip
          label={String(item.status || "").toUpperCase()}
          color={
            item.status === "used"
              ? "warning"
              : item.status === "expired"
                ? "default"
                : "success"
          }
          size="small"
        />
      )
    },
    {
      key: "created_at",
      label: "Created",
      render: (item) => formatDateTime(item.created_at)
    },
    {
      key: "used_at",
      label: "Used / Expired",
      render: (item) => formatDateTime(item.used_at || item.expires_at)
    }
  ];

  return (
    <Box sx={adminPageSx}>
      <AdminPageContainer>
        <Typography variant="h5" fontWeight={700} sx={{ color: Colors.accent, mb: 3 }}>
          Presenter Accounts
        </Typography>

        <Card sx={{ p: 3, mb: 3, ...contentCardSx }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems={{ xs: "flex-start", md: "center" }}>
            <Box>
              <Typography variant="overline" sx={{ color: "text.secondary" }}>
                Total Presenter Accounts
              </Typography>
              <Typography variant="h4" fontWeight={800}>
                {presenterAccounts.length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="overline" sx={{ color: "text.secondary" }}>
                Active Accounts
              </Typography>
              <Typography variant="h4" fontWeight={800}>
                {activeCount}
              </Typography>
            </Box>
            <Alert severity="info" sx={{ flex: 1 }}>
              Presenter accounts are single-use and can only run their assigned draw.
            </Alert>
          </Stack>
        </Card>

        {latestCredentials ? (
          <Card sx={{ p: 3, mb: 3, ...contentCardSx, border: "1px solid rgba(255,193,7,0.45)" }}>
            <Stack spacing={2}>
              <Typography variant="h6" fontWeight={700}>
                Newly Generated Credentials
              </Typography>
              <Alert severity="warning">
                Copy these credentials now. The password is only shown once for security reasons.
              </Alert>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <Stack flex={1} spacing={1}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Presenter ID
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontWeight={700} sx={textWrapSx}>
                      {latestCredentials.presenter_id}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => copyValue("Presenter ID", latestCredentials.presenter_id)}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
                <Stack flex={1} spacing={1}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Password
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontWeight={700} sx={textWrapSx}>
                      {latestCredentials.password}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => copyValue("Password", latestCredentials.password)}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </Card>
        ) : null}

        <Card sx={{ p: { xs: 2, sm: 3 }, mb: 3, ...contentCardSx }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            Create Presenter Account
          </Typography>

          <Stack sx={filterStackSx}>
            <TextField
              select
              label="Assigned Draw"
              value={formData.assigned_draw_id}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  assigned_draw_id: event.target.value
                }))
              }
              fullWidth
            >
              {drawOptions.map((draw) => (
                <MenuItem key={draw.id} value={draw.id}>
                  {draw.draw_code} - {formatDateTime(draw.draw_date)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 3 }}>
            <Button
              variant="contained"
              onClick={createPresenterAccount}
              disabled={saving}
              sx={{
                bgcolor: Colors.accent,
                color: "#002b45",
                fontWeight: 800
              }}
            >
              {saving ? "Generating..." : "Generate Presenter Credentials"}
            </Button>
            <Button variant="outlined" onClick={() => setLatestCredentials(null)}>
              Clear Credential Card
            </Button>
          </Stack>
        </Card>

        <Card sx={{ p: { xs: 2, sm: 3 }, ...contentCardSx, position: "relative" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            sx={{ mb: 2 }}
          >
            <Typography variant="h6" fontWeight={700}>
              Presenter Account History
            </Typography>
            <TextField
              label="Search Presenter ID / Draw"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ minWidth: { md: 300 } }}
            />
          </Stack>

          {loading ? <TableLoadingOverlay label="Loading presenter accounts..." /> : null}

          <ResponsiveDataTable
            columns={presenterColumns}
            rows={filteredAccounts}
            emptyText="No presenter accounts created yet."
          />
        </Card>
      </AdminPageContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
